import React from 'react';
import styled from 'styled-components';
import CloseIcon from '@material-ui/icons/Close';
import { BuilderElement } from './elements';
import { ptToPx } from './coords';
import { offsetsOf, totalOf } from '../../matrixGrid';

interface Props {
  el: BuilderElement;
  selected: boolean;
  /**
   * Belongs to the selected field but is not the active element — i.e. another
   * box of the selected checkbox/radio group. Drawn between the selected and
   * idle styles so the group still reads as a unit while only one box is live.
   */
  related?: boolean;
  /**
   * The field is marked Required (FieldEditorCard). Drawn in the warning
   * (amber) palette so required fields stand out on the page at a glance.
   */
  required?: boolean;
  /**
   * Required only for some outcomes (FieldEditorCard "By result") — the
   * outcomes as words, e.g. 'Partial Pass or Failed'. Amber like a required
   * field, with a dashed edge to say "sometimes".
   */
  requiredWhen?: string;
  onDelete: () => void;
  /**
   * Grab a column or row divider. Matrix only. The canvas owns the drag because
   * it owns `data` and the zoom needed to turn screen pixels into millimetres.
   */
  onTrackResizeStart?: (axis: 'col' | 'row', index: number, e: React.MouseEvent) => void;
}

// Presentational chip rendered as the child of an <Rnd> by FormBuilderCanvas.
// It fills the Rnd box (100% w/h). Text chips render their live text at the real
// font size so the canvas visually matches the generated PDF.
const FieldChip = ({
  el,
  selected,
  related = false,
  required = false,
  requiredWhen = '',
  onDelete,
  onTrackResizeStart,
}: Props) => {
  const isSignature = el.kind === 'signature';
  const isCheckbox = el.kind === 'checkbox';
  const isMatrix = el.kind === 'matrix';

  // Percentages, so the lines stay put at any zoom without recomputing.
  const colWidths = el.colWidthsMm || [];
  const rowHeights = el.rowHeightsMm || [];
  const totalW = totalOf(colWidths) || 1;
  const totalH = totalOf(rowHeights) || 1;
  const colPct = offsetsOf(colWidths).map(mm => (mm / totalW) * 100);
  const rowPct = offsetsOf(rowHeights).map(mm => (mm / totalH) * 100);

  // Dragging a divider resizes the track to its LEFT / ABOVE, the way every
  // spreadsheet behaves. Internal boundaries only: the outer edges are the
  // table's own position and its Rnd resize handles.
  const grab = (axis: 'col' | 'row', index: number) => (e: React.MouseEvent) => {
    if (!onTrackResizeStart) return;
    e.stopPropagation();
    e.preventDefault();
    onTrackResizeStart(axis, index, e);
  };

  const fontPx = ptToPx(el.fontPt);
  const checked = `${el.text}`.trim() === 'x';
  const conditional = !required && !!requiredWhen;

  let title = el.label;
  if (required) title = `${el.label} (required)`;
  else if (conditional) title = `${el.label} (required if ${requiredWhen})`;

  return (
    <Chip
      $selected={selected}
      $related={related}
      $signature={isSignature}
      $checkbox={isCheckbox}
      $required={required || conditional}
      $conditional={conditional}
      title={title}
    >
      {/* Not colour alone: the star says "required" to anyone who cannot tell
          amber from blue. One per field — on a checkbox group only the first
          box carries it, the rest are amber like their siblings. */}
      {(required || conditional) && !el.itemIndex && <RequiredBadge aria-hidden>*</RequiredBadge>}
      {/* The real grid. Without it the table was one flat rectangle, so there
          was nothing to line up against the printed rules underneath. */}
      {isMatrix && (
        <Grid>
          {colPct.slice(1, -1).map((pct, i) => (
            <GridLineV key={`c${colPct[i + 1]}`} style={{ left: `${pct}%` }} />
          ))}
          {rowPct.slice(1, -1).map((pct, i) => (
            <GridLineH key={`r${rowPct[i + 1]}`} style={{ top: `${pct}%` }} />
          ))}
        </Grid>
      )}

      {isMatrix && selected && onTrackResizeStart && (
        <>
          {colPct.slice(1, -1).map((pct, i) => (
            <ColGrip
              key={`cg${colPct[i + 1]}`}
              className="react-draggable-cancel"
              style={{ left: `${pct}%` }}
              onMouseDown={grab('col', i)}
              title={`Column ${i + 1} width — drag to match the printed box`}
            />
          ))}
          {rowPct.slice(1, -1).map((pct, i) => (
            <RowGrip
              key={`rg${rowPct[i + 1]}`}
              className="react-draggable-cancel"
              style={{ top: `${pct}%` }}
              onMouseDown={grab('row', i)}
              title={`Row ${i + 1} height — drag to match the printed box`}
            />
          ))}
        </>
      )}

      {isCheckbox ? (
        <Control $radio={!!el.isRadio} $px={fontPx}>
          {checked && (el.isRadio ? <Dot $px={fontPx} /> : <XMark $px={fontPx}>✕</XMark>)}
        </Control>
      ) : (
        <Text $signature={isSignature} $fontPx={fontPx} $wrap={!!el.multiline}>
          {el.text}
        </Text>
      )}
      {selected && (
        <DeleteBtn
          className="react-draggable-cancel"
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={`Delete ${el.label}`}
        >
          <CloseIcon style={{ fontSize: 12 }} />
        </DeleteBtn>
      )}
    </Chip>
  );
};

export default FieldChip;

type ChipProps = {
  $selected: boolean;
  $related: boolean;
  $signature: boolean;
  $checkbox: boolean;
  $required: boolean;
  $conditional?: boolean;
};

// Amber = the theme's --warning-500 (#e0a012), as rgba so the scan shows through.
const REQ = (alpha: number) => `rgba(224,160,18,${alpha})`;

// Three states, strongest first: active element, sibling of the selected group,
// idle. Written as functions rather than nested ternaries so the middle state
// stays legible.
const chipBorder = ({ $selected, $related, $required }: ChipProps) => {
  // Selection still wins the outline, so the active field is always obvious;
  // a selected required field stays amber inside (chipBackground).
  if ($selected) return 'var(--primary-600)';
  if ($required) return REQ(0.95);
  if ($related) return 'rgba(11,107,155,0.7)';
  return 'rgba(11,107,155,0.45)';
};

const chipBackground = ({ $selected, $related, $signature, $required }: ChipProps) => {
  if ($signature) return 'rgba(11,107,155,0.06)';
  if ($required) return $selected || $related ? REQ(0.26) : REQ(0.16);
  if ($selected) return 'rgba(11,107,155,0.14)';
  if ($related) return 'rgba(11,107,155,0.1)';
  return 'rgba(11,107,155,0.07)';
};

const chipShadow = ({ $selected, $related }: ChipProps) => {
  if ($selected) return 'var(--shadow-focus)';
  if ($related) return '0 0 0 1px rgba(11,107,155,0.28)';
  return 'none';
};

const Chip = styled.div<ChipProps>`
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: ${({ $signature }) => ($signature ? 'center' : 'flex-start')};
  justify-content: ${({ $signature, $checkbox }) => ($signature || $checkbox ? 'center' : 'flex-start')};
  cursor: move;
  border-radius: 3px;
  padding: 0 1px;
  border: 1px solid ${chipBorder};
  background: ${chipBackground};
  box-shadow: ${chipShadow};
  transition: background-color 0.1s ease, border-color 0.1s ease;

  ${({ $signature }) =>
    $signature &&
    `border-style: dashed;
     font-style: italic;`}

  ${({ $conditional }) => $conditional && 'border-style: dashed;'}

  &:hover {
    border-color: var(--primary-600);
  }
`;

const Grid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const GridLineV = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(11, 107, 155, 0.45);
`;

const GridLineH = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(11, 107, 155, 0.3);
`;

// Deliberately wider than the line it sits on: at fit-to-width zoom a column
// rule is about one pixel, which is not a target anyone can hit.
const ColGrip = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 9px;
  margin-left: -4px;
  cursor: col-resize;
  z-index: 3;

  &:hover::after,
  &:active::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 3px;
    width: 3px;
    background: var(--primary-600);
  }
`;

const RowGrip = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 9px;
  margin-top: -4px;
  cursor: row-resize;
  z-index: 3;

  &:hover::after,
  &:active::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 3px;
    height: 3px;
    background: var(--primary-600);
  }
`;

const Control = styled.span<{ $radio: boolean; $px: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${({ $px }) => $px}px;
  height: ${({ $px }) => $px}px;
  border: 1px solid var(--neutral-800);
  border-radius: ${({ $radio }) => ($radio ? '50%' : '2px')};
  background: var(--neutral-0);
  box-sizing: border-box;
  pointer-events: none;
`;

const Dot = styled.span<{ $px: number }>`
  width: ${({ $px }) => $px * 0.5}px;
  height: ${({ $px }) => $px * 0.5}px;
  border-radius: 50%;
  background: var(--neutral-800);
`;

const XMark = styled.span<{ $px: number }>`
  font-size: ${({ $px }) => $px * 0.85}px;
  line-height: 1;
  color: var(--neutral-800);
`;

const Text = styled.span<{ $signature: boolean; $fontPx: number; $wrap?: boolean }>`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: ${({ $fontPx }) => $fontPx}px;
  /* 1.15 is jsPDF's line-height factor, so wrapped lines sit where the PDF
     will put them. */
  line-height: ${({ $fontPx }) => $fontPx * 1.15}px;
  color: ${({ $signature }) => ($signature ? 'var(--primary-700)' : 'var(--neutral-900)')};
  white-space: ${({ $wrap }) => ($wrap ? 'pre-wrap' : 'nowrap')};
  ${({ $wrap }) => $wrap && 'overflow-wrap: anywhere; width: 100%;'}
  overflow: hidden;
  text-overflow: clip;
  pointer-events: none;
  user-select: none;
`;

const RequiredBadge = styled.span`
  position: absolute;
  top: -8px;
  left: -8px;
  z-index: 1;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--warning-500, #e0a012);
  color: #fff;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  font-style: normal;
  pointer-events: none;
`;

const DeleteBtn = styled.button`
  position: absolute;
  top: -9px;
  right: -9px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--danger-500, #d44b3f);
  color: #fff;
  cursor: pointer;
  box-shadow: var(--shadow-xs);
  z-index: 3;

  &:hover {
    background: var(--danger-600, #b83a30);
  }
`;
